// src/contexts/AuthContext.jsx
import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { API_BASE_URL } from '../lib/apiBaseUrl';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check admin status with backend
  const checkAdminStatus = async (firebaseUser) => {
    if (!firebaseUser) {
      setIsAdmin(false);
      return false;
    }

    try {
      const token = await firebaseUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/admin/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        const data = await response.json();
        setIsAdmin(true);
        setError(null);
        return true;
      } else if (response.status === 403) {
        setIsAdmin(false);
        setError('Not authorized as admin');
        // Auto-logout non-admin users
        await signOut(auth);
        return false;
      } else if (response.status === 401) {
        setIsAdmin(false);
        setError('Authentication failed');
        await signOut(auth);
        return false;
      } else {
        setIsAdmin(false);
        setError('Admin check failed');
        return false;
      }
    } catch (err) {
      console.error('Admin status check failed:', err);
      setIsAdmin(false);
      setError('Network error during admin check');
      return false;
    }
  };

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      setUser(firebaseUser);

      if (firebaseUser) {
        // User is logged in, check admin status
        await checkAdminStatus(firebaseUser);
      } else {
        // User is logged out
        setIsAdmin(false);
        setError(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Check admin status after login
      const isAdminUser = await checkAdminStatus(result.user);

      if (!isAdminUser) {
        // If not admin, sign out immediately
        await signOut(auth);
        throw new Error('This account does not have admin privileges');
      }

      setLoading(false);
      return { success: true };
    } catch (err) {
      setLoading(false);

      // Handle specific Firebase auth errors
      let errorMessage = 'Login failed';

      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout function
  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
      setError(null);
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Logout failed');
    } finally {
      setLoading(false);
    }
  };

  // Get current ID token
  const getIdToken = async () => {
    if (!user) {
      throw new Error('No authenticated user');
    }
    return await user.getIdToken();
  };

  const value = {
    user,
    isAdmin,
    loading,
    error,
    login,
    logout,
    getIdToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
